import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import catAffordable from '@/static/assets/pet/cat/affordable.svg';
import catAffordableMobile from '@/static/assets/pet/cat/affordable_mobile.svg';
import catFastAndSimple from '@/static/assets/pet/cat/fast_and_simple.svg';
import catFastAndSimpleMobile from '@/static/assets/pet/cat/fast_and_simple_mobile.svg';
import catPersonal from '@/static/assets/pet/cat/personal.svg';
import catPersonalMobile from '@/static/assets/pet/cat/personal_mobile.svg';
import petAffordable from '@/static/assets/pet/dog/affordable.svg';
import petAffordableMobile from '@/static/assets/pet/dog/affordable_mobile.svg';
import petFastAndSimple from '@/static/assets/pet/dog/fast_and_simple.svg';
import petFastAndSimpleMobile from '@/static/assets/pet/dog/fast_and_simple_mobile.svg';
import petPersonal from '@/static/assets/pet/dog/personal.svg';
import petPersonalMobile from '@/static/assets/pet/dog/personal_mobile.svg';
import { Flex, ResponsiveBreak, font, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import Image from 'next/image';
import type React from 'react';
import styled from 'styled-components';
import { PetLandingPageType } from './shared';

const ValuePointsWrapper = styled(Section).attrs({ darkBackground: true })`
  width: 100%;
`;

const Heading = styled(SectionTitle)`
  margin-bottom: 24px;
  text-transform: capitalize;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 16px;
    `}
`;

const SubHeading = styled(SectionParagraph)`
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 48px;
    `}
`;

export const MainCTA = styled(JoinCTA)`
  width: 265px;
  margin-bottom: 24px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        margin-bottom: 48px;
    `}
`;

const Values = styled(Flex)`
  margin: 0;
  justify-content: flex-start;
  width: 100%;
  flex-direction: column;
  gap: 48px;

  ${MEDIA_QUERIES.desktop`
        max-width: 1140px;
        flex-direction: row;
    `};
`;

const ValueListItem = styled(Flex).attrs({
  flexDirection: 'column',
  alignItems: 'center',
})`
  flex: 1 0 260px;
`;

const IllustrationWrapper = styled(Flex).attrs({
  alignItems: 'center',
  justifyContent: 'center',
})`
  width: 165px;
  height: 165px;
  margin-bottom: 24px;
  background-color: transparent;

  & > img {
    max-width: 100%;
    object-fit: cover;
  }

  & > img[data-display='desktop'] {
    display: none;
  }

  & > img[data-display='mobile'] {
    display: block;
  }

  ${MEDIA_QUERIES.tabletAndDesktop`
        width: 175px;
        height: 175px;

        & > img[data-display='desktop'] {
            display: block;
        }

        & > img[data-display='mobile'] {
            display: none;
        }
    `}
`;

const ValueHeading = styled.p`
  text-align: center;
  ${font('main', { fontSize: '2rem', lineHeight: '1.2', fontWeight: 700 })};
  text-transform: capitalize;
  margin-bottom: 16px;

  ${MEDIA_QUERIES.tabletAndDesktop`
        font-size: 2.5rem
    `}
`;

const ValueDescription = styled.p`
  ${font('main', { fontSize: '1.4rem', lineHeight: '1.5' })};
  text-align: center;
  color: ${themedColor('dark')};

  ${MEDIA_QUERIES.tabletAndDesktop`
        font-size: 1.8rem;
        max-width: 308px;
    `}
`;

const values = [
  {
    title: {
      [PetLandingPageType.Dog]: 'affordable',
      [PetLandingPageType.Cat]: 'affordable',
      [PetLandingPageType.PetInsurance]: 'affordable',
    },
    description: {
      [PetLandingPageType.Dog]: (
        <>
          <span>Competitive rates. You pay</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>for the health plan your dog</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>really needs, and not what</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>you don’t want.</span>
        </>
      ),
      [PetLandingPageType.Cat]: (
        <>
          <span>Pay for the health plan your</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>cat really needs, and not what</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>you don’t want.</span>
        </>
      ),
      [PetLandingPageType.PetInsurance]: (
        <>
          <span>Pay for the health plan your</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>pet really needs, and not what</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>you don’t want</span>
        </>
      ),
    },

    images: {
      [PetLandingPageType.Dog]: {
        desktopSrc: petAffordable,
        mobileSrc: petAffordableMobile,
      },
      [PetLandingPageType.Cat]: {
        desktopSrc: catAffordable,
        mobileSrc: catAffordableMobile,
      },
      [PetLandingPageType.PetInsurance]: {
        desktopSrc: petAffordable,
        mobileSrc: petAffordableMobile,
      },
    },
  },
  {
    title: {
      [PetLandingPageType.Dog]: 'customized',
      [PetLandingPageType.Cat]: 'personalized',
      [PetLandingPageType.PetInsurance]: 'personalized',
    },
    description: {
      [PetLandingPageType.Dog]: (
        <>
          <span>Every dog is unique. Fetch</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>the policy that’s right for</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>your special friend.</span>
        </>
      ),
      [PetLandingPageType.Cat]: (
        <>
          <span>Your cat’s insurance plan should be</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>as unique as they are. Get the</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>coverage that suits your cat’s actual needs.</span>
        </>
      ),
      [PetLandingPageType.PetInsurance]: (
        <>
          <span>No two pets are the same. So why</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>offer them the same insurance</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>plan? Get the coverage</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>your pet actually needs.</span>
        </>
      ),
    },
    images: {
      [PetLandingPageType.Dog]: {
        desktopSrc: petPersonal,
        mobileSrc: petPersonalMobile,
      },
      [PetLandingPageType.Cat]: {
        desktopSrc: catPersonal,
        mobileSrc: catPersonalMobile,
      },
      [PetLandingPageType.PetInsurance]: {
        desktopSrc: petPersonal,
        mobileSrc: petPersonalMobile,
      },
    },
  },
  {
    title: {
      [PetLandingPageType.Dog]: 'fast & simple',
      [PetLandingPageType.Cat]: 'fast & simple',
      [PetLandingPageType.PetInsurance]: ' super fast & simple',
    },
    description: {
      [PetLandingPageType.Dog]: (
        <>
          <span>Easy, digital claims resolution</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>let you spend less time on</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>paperwork and more time</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>on belly scratches.</span>
        </>
      ),
      [PetLandingPageType.Cat]: (
        <>
          <span>Hassle-free, digital claims</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>resolution let you spend less</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>time on paperwork and more</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>time on chin scratches.</span>
        </>
      ),
      [PetLandingPageType.PetInsurance]: (
        <>
          <span>Hassle-free, digital claims</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>resolution let you spend less</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>time on paperwork and more</span>
          <ResponsiveBreak display={['block', 'inline']} />
          <span>time on belly scratches.</span>
        </>
      ),
    },
    images: {
      [PetLandingPageType.Dog]: {
        desktopSrc: petFastAndSimple.src,
        mobileSrc: petFastAndSimpleMobile.src,
      },
      [PetLandingPageType.Cat]: {
        desktopSrc: catFastAndSimple.src,
        mobileSrc: catFastAndSimpleMobile.src,
      },
      [PetLandingPageType.PetInsurance]: {
        desktopSrc: petFastAndSimple.src,
        mobileSrc: petFastAndSimpleMobile.src,
      },
    },
  },
];

const title = {
  [PetLandingPageType.Dog]: 'Dog-Friendly Features',
  [PetLandingPageType.Cat]: 'Coverage That’s The Cat’s Meow',
  [PetLandingPageType.PetInsurance]: 'Pet-Friendly Everything',
};

const subTitle = {
  [PetLandingPageType.Dog]: (
    <>
      <span>
        We make <a href="https://www.lemonade.com/pet">pet insurance</a>
      </span>
      <ResponsiveBreak display={['block', 'inline']} />
      <span>a walk in the park</span>
    </>
  ),
  [PetLandingPageType.Cat]: (
    <span>
      <a href="https://www.lemonade.com/pet">Pet insurance</a> that’s downright delightful
    </span>
  ),
  [PetLandingPageType.PetInsurance]: (
    <>
      <span>With Lemonade, keeping your pet</span>
      <ResponsiveBreak display={['block', 'inline']} />
      <span>healthy is a treat</span>
    </>
  ),
};

interface ValuePointsProps {
  petLandingPageType: PetLandingPageType;
}

const ValuePoints: React.FC<ValuePointsProps> = ({ petLandingPageType }) => {
  return (
    <ValuePointsWrapper justifyContent="center" pb={[40, 70]}>
      <Flex flexDirection="column" alignItems="center" px={[16, 16, 0]}>
        <Heading>{title[petLandingPageType]}</Heading>
        <SubHeading>{subTitle[petLandingPageType]}</SubHeading>
      </Flex>
      <Values px={[60, 60, 0]}>
        {values.map(({ title, description, images }) => (
          <ValueListItem key={title[petLandingPageType]}>
            <IllustrationWrapper>
              <Image
                data-display="desktop"
                alt={title[petLandingPageType]}
                src={images[petLandingPageType].desktopSrc}
                width={165}
                height={165}
              />
              <Image
                data-display="mobile"
                alt={title[petLandingPageType]}
                src={images[petLandingPageType].mobileSrc}
                width={165}
                height={165}
              />
            </IllustrationWrapper>
            <ValueHeading>{title[petLandingPageType]}</ValueHeading>
            <ValueDescription>{description[petLandingPageType]}</ValueDescription>
          </ValueListItem>
        ))}
      </Values>
    </ValuePointsWrapper>
  );
};

export default ValuePoints;
